import circleBg from '@/assets/notifications/order-filled-partial/medallion-circle-bg.svg';
import stackedCoins from '@/assets/notifications/order-filled-partial/medallion-stacked-coins.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketTitle, metaNumber, metaString, priceCentsLabel } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['order-filled-partial'];

/**
 * ORDER_FILLED · partial fill. Neutral/purple tone (a partial fill is
 * informational, not a "win" — no green): purple stacked-coins medallion,
 * "You're in - partly", the market title, the order line, and a filled/waiting
 * breakdown. CTA is Figma's "View Order" → navigateMarket.
 *
 * Meta comes from `orderFilledNotification` (predictions-api): `filledQuantity`
 * (contracts filled so far), `orderQuantity` (total order size), `avgPrice`
 * (normalised to dollars 0–1), `marketName`. There is no `waiting`/`remaining`
 * key — "waiting to match" is `orderQuantity - filledQuantity`. `action`/`side`
 * are copy-vars only (not in meta), so the order verb defaults to "Buy".
 */
export function OrderFilledPartialPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const title = marketTitle(meta);
  const action = metaString(meta, 'action')?.toLowerCase() === 'sell' ? 'Sell' : 'Buy';
  const orderQty = metaNumber(meta, 'orderQuantity');
  const price = metaNumber(meta, 'avgPrice');
  const filled = metaNumber(meta, 'filledQuantity');
  const waiting = orderQty !== undefined && filled !== undefined ? Math.max(0, orderQty - filled) : undefined;

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={stackedCoins} tone="neutral" alt="" />

      <div className="flex flex-col items-center gap-2">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">{`You're in - partly`}</h2>

        {title && <p className="text-xl font-bold text-ink">{`“${title}”`}</p>}

        {(orderQty !== undefined || price !== undefined) && (
          <p className="text-xl text-ink">
            {action} order ·{' '}
            <span className="font-bold">
              {orderQty !== undefined ? `${orderQty} contracts` : 'your contracts'}
              {price !== undefined ? ` @ ${priceCentsLabel(price)}` : ''}
            </span>
          </p>
        )}

        {filled !== undefined && waiting !== undefined && (
          <p className="text-xl text-ink">{`${filled} contracts filled · ${waiting} waiting to match`}</p>
        )}
      </div>

      <PopupCta
        label={CTA.primary.label}
        width={234}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
